import { DocumentData, DocumentSnapshot, QueryDocumentSnapshot, collection, doc, getDocs, getFirestore, updateDoc, writeBatch, Timestamp, setDoc } from "firebase/firestore";
import { DbCollection } from "../constants/db";
import { AddressWithErrors, AddressWithErrorsAndCoordinates, AddressWithErrorsDbData, SensitizationReportDbData, SensitizationReport } from "../models/AwarenessCampaign";
import { AwarenessCampaignsActions } from "../store/reducers/awareness_campaigns/list";
import { AppDispatch } from "../store/store";
import { fetchAPI, handleAPIError } from "./actions";
import { processImageResponses } from "../helpers/storage";
import { ComponentType } from "../models/SurveyTemplates";
import { showError, showSuccess, showTranslatedMessage } from "../store/reducers/snacks";
import { CampaignAddressesActions } from "../store/reducers/awareness_campaigns/addresses";
import i18next, { Namespace } from "../locales/translations";
import { Coordinates, formatAddress } from "../helpers/geo";
import urls from "../constants/urls";
import { stringifyUrl } from "query-string";
import { AddressComponents } from "../models/Address";
import { AddressReportActions } from "../store/reducers/awareness_campaigns/address_report";
import { AmbassadorData } from "../models/User";

/**
 * Serialize an Awareness Campaign's Address data from their database document
 */
function fromDbDoc(dbDoc: QueryDocumentSnapshot<DocumentData>): AddressWithErrors;
function fromDbDoc(dbDoc: DocumentSnapshot<DocumentData>): AddressWithErrors;
function fromDbDoc(dbDoc: QueryDocumentSnapshot<DocumentData> | DocumentSnapshot<DocumentData>) {
    const data = dbDoc.data() as AddressWithErrorsDbData;
    const missionAddressData: AddressWithErrors = {
        ...data,
        ID: dbDoc.id,
        reportFiledAt: data.reportFiledAt?.toDate().toISOString(),
        report: data.report ? {
            ...data.report,
            submittedAt: data.report.submittedAt?.toDate().toISOString(),
        } as SensitizationReport : undefined,
    };

    return missionAddressData;
}

/**
 * Retrieves all addresses for a specific mission.
 * @param ambassadorEmail - The email of the current ambassador user.
 * @param partnerID - The ID of the partner.
 * @param missionID - The ID of the mission.
 * @returns The list of address documents.
 */
const getAddresses = (ambassadorEmail: string, partnerID: string, missionID: string) => async (dispatch: AppDispatch) => {
    dispatch(CampaignAddressesActions.startLoading());

    try {
        const db = getFirestore();
        const addressesRef = collection(db, DbCollection.PARTNERS, partnerID, DbCollection.AWARENESS_CAMPAIGNS, missionID, DbCollection.SENSITIZATION_ADDRESSES);
        const addressesSnapshot = await getDocs(addressesRef);

        const missionAddresses: AddressWithErrors[] = addressesSnapshot.docs
            .map(fromDbDoc)
            .filter(address => address.assigned?.email.toLowerCase() === ambassadorEmail.toLowerCase());

        dispatch(CampaignAddressesActions.setAddresses(missionAddresses));
        return missionAddresses;
    } catch (e) {
        dispatch(handleAPIError(e, "fetching mission addresses", AwarenessCampaignsActions.setError));
        return null;
    }
};

export const optimizeAddressRoute = (
    partnerID: string,
    startingPoint: Coordinates | null,
    addresses: AddressWithErrors[]
) => async (dispatch: AppDispatch) => {
    dispatch(CampaignAddressesActions.startLoading());
    try {
        const nonVisitedAddresses = addresses.filter(address => !address.visited && address.lat && address.lng) as AddressWithErrorsAndCoordinates[];
        const visitedAddresses = addresses.filter(address => address.visited || !address.lat || !address.lng);

        if (nonVisitedAddresses.length === 0) {
            throw new Error('No valid addresses available for route optimization');
        }

        let firstPoint;

        // Determine starting point
        let effectiveStartingPoint: Coordinates;
        if (startingPoint && startingPoint.latitude !== 0 && startingPoint.longitude !== 0) {
            // Use provided starting point if valid
            effectiveStartingPoint = startingPoint;
        } else {
            // Use first valid point as starting point
            firstPoint = nonVisitedAddresses[0];
            effectiveStartingPoint = {
                latitude: firstPoint.lat,
                longitude: firstPoint.lng
            };

            // Remove the first point from the list of points to optimize
            nonVisitedAddresses.shift();
        }

        // Prepare request body with strict type checking
        const points = nonVisitedAddresses.map((address) => ({
            id: address.ID,
            coordinates: {
                latitude: parseFloat(address.lat.toFixed(6)),
                longitude: parseFloat(address.lng.toFixed(6))
            },
        }));

        const response = await fetchAPI(
            `${urls.API}/partner/${partnerID}/awareness-campaign/sort-itinerary`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    startPoint: {
                        latitude: parseFloat(effectiveStartingPoint.latitude.toFixed(6)),
                        longitude: parseFloat(effectiveStartingPoint.longitude.toFixed(6))
                    },
                    points
                }),
            }
        );

        // Add validation for the response
        if (!response || !Array.isArray(response)) {
            console.error("Invalid response format:", response);
            
            // Check if it's an authentication error
            if (response && typeof response === 'object' && 'body' in response && 'code' in response) {
                const errorResponse = response as { body: { code: string; message: string }; code: number };
                if (errorResponse.code === 401 && errorResponse.body?.code?.includes('auth/')) {
                    throw new Error(`Authentication error: ${errorResponse.body.message}`);
                }
            }
            
            throw new Error('Invalid response format from sort-itinerary');
        }

        // Reorder addresses based on optimized route
        const addressMap = new Map(nonVisitedAddresses.map((addr) => [addr.ID, addr]));

        // Map the response to addresses, accounting for the actual response structure
        const sortedAddresses = [];

        for (const item of response) {
            if (!item || typeof item !== 'object' || !('id' in item)) {
                console.error(`Invalid item in response: ${JSON.stringify(item)}`);
                throw new Error(`Invalid item format in response`);
            }

            const id = item.id;
            const address = addressMap.get(id);

            if (!address) {
                console.error(`Address lookup failed for ID: ${id}`);
                console.error(`Available IDs in map: ${Array.from(addressMap.keys()).join(', ')}`);
                throw new Error(`Address ${id} not found in original list`);
            }

            sortedAddresses.push(address);
        }

        // If firstPoint exists, add it to the beginning of the sorted addresses
        if (firstPoint) {
            sortedAddresses.unshift(firstPoint);
        }

        // Combine sorted non-visited addresses with visited addresses at the end
        const finalSortedAddresses = [...sortedAddresses, ...visitedAddresses];

        // Update Redux state
        dispatch(CampaignAddressesActions.setSortedAddresses(finalSortedAddresses));

        // Show success message
        dispatch(showSuccess(i18next.t('address_sorted_successfully', { ns: Namespace.SNACKS })))

        return sortedAddresses;
    } catch (e) {
        const error = e as Error;
        console.error("Route Optimization Error:", error.message);
        dispatch(showError(error.message));
        dispatch(CampaignAddressesActions.stopLoading());
        return null;
    }
};


/**
 * Action to add a sensitized address with dynamic survey template data.
 * 
 * @param partnerID - The ID of the partner.
 * @param missionID - The ID of the mission.
 * @param addressData - The address data to add.
 * @param report - The SensitizationReport data.
 * @param ambassador - The ambassador data.
 */
const addSensitizedAddressWithTemplate = (
    partnerID: string,
    missionID: string,
    addressData: Pick<AddressWithErrors, 'address' | 'lat' | 'lng'>,
    report: SensitizationReportDbData,
    ambassador: AmbassadorData,
) => async (dispatch: AppDispatch) => {

    try {
        dispatch(CampaignAddressesActions.startLoading());

        const db = getFirestore();

        // Reference to the 'sensitization_addresses' subcollection
        const addressesCollectionRef = collection(
            db,
            DbCollection.PARTNERS,
            partnerID,
            DbCollection.AWARENESS_CAMPAIGNS,
            missionID,
            DbCollection.SENSITIZATION_ADDRESSES
        );

        const newDocRef = doc(addressesCollectionRef);
        const addressID = newDocRef.id;

        // Extract images from the report responses if they exist
        const imageResponses = Object.values(report.responses).filter(
            (response) => response.componentType === ComponentType.IMAGE_UPLOAD && Array.isArray(response.value)
        );
        
        // Process image responses using the reusable function
        await processImageResponses(missionID, addressID, imageResponses);

        const newAddress: AddressWithErrorsDbData = {
            ...addressData,
            addressKey: formatAddress(addressData.address),
            ID: addressID,
            partnerID: partnerID,
            errors: {},
            errorsCount: 0,
            report: report,
            reportFiledAt: Timestamp.now(),
            visited: true,
            assigned: ambassador,
            fromExternalSource: true,
        };

        // Update the address document with report data
        await setDoc(newDocRef, newAddress);

        dispatch(CampaignAddressesActions.addAddresses([{
            ...newAddress,
            reportFiledAt: newAddress.reportFiledAt?.toDate().toISOString(),
            report: newAddress.report ? {
                ...newAddress.report,
                submittedAt: newAddress.report.submittedAt?.toDate().toISOString(),
            } as SensitizationReport : undefined,
        }]));
        dispatch(showSuccess(i18next.t('address_report_created', { ns: Namespace.SNACKS })));

    } catch (error) {
        let errorMessage = i18next.t('unknown_error', { ns: Namespace.SNACKS });

        dispatch(handleAPIError(error, errorMessage, AwarenessCampaignsActions.setError));
    } finally {
        dispatch(CampaignAddressesActions.stopLoading());
    }
};

/**
 * Action to update an address with dynamic survey template data.
 * 
 * @param partnerID - The ID of the partner.
 * @param missionID - The ID of the mission.
 * @param addressID - The ID of the address to update.
 * @param report - The SensitizationReport data.
 */
const updateCampaignAddressWithTemplate = (
    partnerID: string,
    missionID: string,
    addressID: string,
    report: SensitizationReportDbData
) => async (dispatch: AppDispatch) => {

    try {
        dispatch(CampaignAddressesActions.startLoading());
        const db = getFirestore();

        // Reference to the 'sensitization_addresses' subcollection
        const addressesCollectionRef = collection(
            db,
            DbCollection.PARTNERS,
            partnerID,
            DbCollection.AWARENESS_CAMPAIGNS,
            missionID,
            DbCollection.SENSITIZATION_ADDRESSES
        );

        // Reference to the specific address document
        const addressDocRef = doc(addressesCollectionRef, addressID);

        // Extract images from the report responses if they exist
        const imageResponses = Object.values(report.responses).filter(
            (response) => response.componentType === ComponentType.IMAGE_UPLOAD && Array.isArray(response.value)
        );
        
        // Process image responses using the reusable function
        await processImageResponses(missionID, addressID, imageResponses);

        // Update the address document with report data
        const updatedAddress: Pick<AddressWithErrorsDbData, 'visited' | 'report' | 'reportFiledAt'> = {
            visited: true,
            report: report,
            reportFiledAt: Timestamp.now(),
        };

        await updateDoc(addressDocRef, updatedAddress);

        dispatch(CampaignAddressesActions.updateMany([{
            id: addressID,
            changes: {
                ...updatedAddress,
                reportFiledAt: updatedAddress.reportFiledAt?.toDate().toISOString(),
                report: updatedAddress.report ? {
                    ...updatedAddress.report,
                    submittedAt: updatedAddress.report.submittedAt?.toDate().toISOString(),
                } as SensitizationReport : undefined,
            }
        }]));

        dispatch(showSuccess(i18next.t('address_report_updated', { ns: Namespace.SNACKS })));

    } catch (error) {
        let errorMessage = i18next.t('unknown_error', { ns: Namespace.SNACKS });

        dispatch(handleAPIError(error, errorMessage, AwarenessCampaignsActions.setError));
    } finally {
        dispatch(CampaignAddressesActions.stopLoading());
    }
};

/**
 * Mark all non-visited addresses as visited with a default report when completing a mission
 * @param partnerID - The ID of the partner
 * @param missionID - The ID of the mission
 * @param nonVisitedAddresses - Array of non-visited addresses to mark as visited
 * @returns A promise that resolves when all addresses are marked as visited
 */
const markAddressesAsVisitedForMissionCompletion = (
    partnerID: string,
    missionID: string,
    nonVisitedAddresses: AddressWithErrors[]
) => async (dispatch: AppDispatch) => {
    dispatch(CampaignAddressesActions.startLoading());

    try {
        if (nonVisitedAddresses.length === 0) {
            return true; // Nothing to update
        }

        // Use Firestore batch for bulk updates
        const db = getFirestore();
        const batch = writeBatch(db);

        const now = Timestamp.now();

        // Create sample report data - keeping the old format for backward compatibility
        const sampleReportData: SensitizationReportDbData = {
            templateID: '',
            responses: {},
            submittedAt: now,
            submittedBy: {
                name: '',
                email: ''
            },
            isComplete: false
        };

        const updatedAddressData: Pick<AddressWithErrorsDbData, 'visited' | 'report' | 'reportFiledAt'> = {
            visited: true,
            report: sampleReportData,
            reportFiledAt: now,
        };

        // Add each address update to the batch
        nonVisitedAddresses.forEach(address => {
            const addressRef = doc(
                db,
                DbCollection.PARTNERS,
                partnerID,
                DbCollection.AWARENESS_CAMPAIGNS,
                missionID,
                DbCollection.SENSITIZATION_ADDRESSES,
                address.ID
            );

            batch.update(addressRef, updatedAddressData);
        });

        // Commit the batch
        await batch.commit();

        // Update all addresses in Redux state
        dispatch(CampaignAddressesActions.updateMany(
            nonVisitedAddresses.map(address => ({
                id: address.ID,
                changes: {
                    ...updatedAddressData,
                    reportFiledAt: now.toDate().toISOString(),
                    report: updatedAddressData.report ? {
                        ...updatedAddressData.report,
                        submittedAt: updatedAddressData.report.submittedAt?.toDate().toISOString(),
                    } as SensitizationReport : undefined,
                }
            }))
        ));

        // Show success message
        dispatch(showTranslatedMessage({
            variant: 'success',
            messageKey: 'addresses_bulk_updated',
            context: { count: nonVisitedAddresses.length }
        }));

        return true;
    } catch (error) {
        console.error('Error marking addresses as visited:', error);
        dispatch(showError(i18next.t('error_updating_addresses', { ns: Namespace.SNACKS })));
        return false;
    } finally {
        dispatch(CampaignAddressesActions.stopLoading());
    }
};

/**
 * Reverse geocode a latitude and longitude to get an address
 * @param partnerID - The ID of the partner
 * @param latitude - The latitude of the address
 * @param longitude - The longitude of the address
 * @returns The address of the latitude and longitude
 */
const reverseGeocodeAddress = (partnerID: string, latitude: number, longitude: number) => async (dispatch: AppDispatch) => {
    try {
        dispatch(AddressReportActions.startLoadingUserAddress());

        const url = stringifyUrl({
            url: `${urls.API}/partner/${partnerID}/addresses/lookup`,
            query: {
                lat: latitude,
                lng: longitude
            }
        });
        const response: { address: AddressComponents }[] = await fetchAPI(url);

        dispatch(AddressReportActions.setUserAddress(response[0]?.address ?? null));

        return response[0]?.address ?? null;
    } catch (error) {
        console.error('Error reverse geocoding address:', error);
        dispatch(AddressReportActions.stopLoadingUserAddress());
        dispatch(showError(i18next.t('error_reverse_geocoding_address', { ns: Namespace.SNACKS })));
        return null;
    }
}

const CampaignAddressesMethods = {
    getAddresses,
    addSensitizedAddressWithTemplate,
    updateCampaignAddressWithTemplate,
    optimizeAddressRoute,
    markAddressesAsVisitedForMissionCompletion,
    reverseGeocodeAddress,
};

export default CampaignAddressesMethods;
